/* facets.js — <facet-filters> custom element. Async collection/search filtering + sort
   via the Section Rendering API, with a mobile filter drawer. Framework-free, deferred.
   No-JS fallback: the form submits as a normal GET and the page reloads. */
(function () {
  'use strict';

  // change, submit, and click are delegated on <facet-filters>. The section
  // render replaces [data-collection-inner], which holds the form and the sort
  // <select>. Nothing listens on those nodes. keydown and popstate listen on
  // document and window once, and look up the live element when they fire.
  var documentListenersBound = false;

  function bindDocumentListeners() {
    if (documentListenersBound) return;
    documentListenersBound = true;
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('facet-filters.is-open');
      if (open) open.closeDrawer();
    });
    window.addEventListener('popstate', function () {
      document.querySelectorAll('facet-filters').forEach(function (el) {
        el.render(window.location.href, false);
      });
    });
  }

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      bindDocumentListeners();
      if (this._delegated) return;
      this._delegated = true;

      // Auto-apply on any control change (checkbox, price, sort).
      // The form node is replaced on every section render, so the handler
      // only decides to run. apply() reads the live form and select.
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-facet-form]') || e.target.matches('[name="sort_by"]')) {
          this.apply();
        }
      });
      // Submit (no-JS button or Enter) → intercept for async.
      this.addEventListener('submit', (e) => { e.preventDefault(); this.apply(); });
      // Clear all + drawer open/close (toggle button opens; overlay + X close).
      this.addEventListener('click', (e) => {
        var pageLink = e.target.closest('.pagination a[href]');
        if (pageLink) {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          this.navigate(pageLink.getAttribute('href'), true);
          return;
        }
        var more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); return; }
        var clear = e.target.closest('[data-facet-clear]');
        if (clear) {
          e.preventDefault();
          // Chips remove one filter and leave the drawer alone. Clear all
          // inside the drawer closes it. The clicked link is replaced, so
          // focus returns to the Filters button after the new toolbar exists.
          var inDrawer = !!clear.closest('.collection-filters');
          var restoreFocus = inDrawer && !clear.classList.contains('collection-chip');
          // Clear all closes first, then scrolls with the new results.
          // Drop a pending drawer scroll so close does not jump twice.
          if (!clear.classList.contains('collection-chip')) {
            this._scrollOnClose = false;
            this.closeDrawer();
          }
          this._focusFilters = restoreFocus;
          this.navigate(clear.getAttribute('href'), !this.classList.contains('is-open'));
          return;
        }
        var facetMore = e.target.closest('[data-facet-more]');
        if (facetMore) {
          e.preventDefault();
          this.toggleMore(facetMore);
          return;
        }
        var toggle = e.target.closest('[data-facet-toggle]');
        if (toggle) {
          this._filtersOpener = toggle;
          this.openDrawer();
        }
        if (e.target.closest('[data-facet-close]')) this.closeDrawer();
      });
      this.syncDrawer();
    }

    drawerPanel() {
      return this.querySelector('.collection-filters');
    }

    // Closed panel is inert and aria-hidden so it leaves the tab order and
    // the accessibility tree. Both come off while the drawer is open. The
    // page behind stays as it is: the overlay catches pointer input.
    syncDrawer() {
      var open = this.classList.contains('is-open');
      var panel = this.drawerPanel();
      if (panel) {
        if (open) {
          panel.removeAttribute('aria-hidden');
          panel.removeAttribute('inert');
        } else {
          panel.setAttribute('aria-hidden', 'true');
          panel.setAttribute('inert', '');
        }
      }
      var buttons = this.querySelectorAll('[data-facet-toggle]');
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    }

    filtersTrigger() {
      if (this._filtersOpener && this._filtersOpener.isConnected) return this._filtersOpener;
      return this.querySelector('[data-facet-toggle]');
    }

    openDrawer() {
      if (!this._filtersOpener || !this._filtersOpener.isConnected) {
        this._filtersOpener = this.querySelector('[data-facet-toggle]');
      }
      this.classList.add('is-open');
      this.filtersOpen = true;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('filters-open');
      document.documentElement.setAttribute('data-filters-open', 'true');
      this.syncDrawer();
    }

    closeDrawer() {
      var wasOpen = this.classList.contains('is-open');
      // Move focus before the panel is hidden. Chrome blocks aria-hidden
      // on an ancestor that still contains the focused control.
      var trigger = this.filtersTrigger();
      if (wasOpen && trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
      var panel = this.drawerPanel();
      if (panel && document.activeElement && panel.contains(document.activeElement) && document.activeElement.blur) {
        document.activeElement.blur();
      }
      this.classList.remove('is-open');
      this.filtersOpen = false;
      document.body.style.overflow = '';
      document.body.classList.remove('filters-open');
      document.documentElement.removeAttribute('data-filters-open');
      this.syncDrawer();
      // A filter change while the drawer is open waits to scroll until the
      // drawer closes, so the page does not jump under the open panel.
      if (wasOpen && this._scrollOnClose && this.isConnected) {
        this._scrollOnClose = false;
        this.scrollToResults();
      }
    }

    disconnectedCallback() {
      if (this.filtersOpen) this.closeDrawer();
    }

    toggleMore(button) {
      var panelId = button.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;
      var open = button.getAttribute('aria-expanded') === 'true';
      if (open) {
        panel.classList.add('is-collapsed');
        button.setAttribute('aria-expanded', 'false');
        button.textContent = button.getAttribute('data-label-more') || '';
      } else {
        panel.classList.remove('is-collapsed');
        button.setAttribute('aria-expanded', 'true');
        button.textContent = button.getAttribute('data-label-less') || '';
      }
    }

    apply() {
      // The sort <select> sits outside #FacetForm and points at it with form=.
      // While the filters drawer is translated off-canvas, some browsers drop
      // that association and FormData omits sort_by. Read the select itself.
      // The form node is replaced on every section render, so do not cache it.
      var form = this.querySelector('[data-facet-form]');
      // A closed panel is inert. Lift that only for this read so FormData
      // still sees the checked boxes, then put it back before yielding.
      var panel = this.drawerPanel();
      var restoreInert = !!(panel && panel.hasAttribute('inert'));
      if (restoreInert) panel.removeAttribute('inert');
      var params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams(window.location.search);
      if (restoreInert) panel.setAttribute('inert', '');
      var sort = this.querySelector('select[name="sort_by"]');
      if (sort) {
        if (sort.value) params.set('sort_by', sort.value);
        else params.delete('sort_by');
      }
      Array.from(params.entries()).forEach(function (entry) {
        if (entry[1] === '') params.delete(entry[0]);
      });
      // Closed drawer: keep a URL filter only when the live form has no field
      // for it. An unchecked box is in the form and must stay off. An open
      // drawer uses the form alone.
      if (form && !this.classList.contains('is-open')) {
        var fieldNames = {};
        Array.from(form.elements).forEach(function (el) {
          if (el.name) fieldNames[el.name] = true;
        });
        var current = new URLSearchParams(window.location.search);
        current.forEach(function (value, key) {
          if (key === 'sort_by' || key === 'page' || key === 'section_id') return;
          if (value !== '' && !params.has(key) && !fieldNames[key]) params.append(key, value);
        });
      }
      // Filter and sort changes start again at page 1. Page links keep the query.
      params.delete('page');
      var action = (form && form.getAttribute('action')) || window.location.pathname;
      var qs = params.toString();
      // Skip the scroll while the drawer is open. Sort and Clear all (the
      // drawer closes first) still scroll. A filter change scrolls once,
      // when the drawer closes.
      var drawerOpen = this.classList.contains('is-open');
      if (drawerOpen) this._scrollOnClose = true;
      this.navigate(qs ? action + '?' + qs : action, !drawerOpen);
    }

    navigate(url, scroll) {
      this.render(url, true, scroll);
    }

    loadMore(trigger) {
      if (trigger.getAttribute('aria-busy') === 'true') return;
      var url = trigger.getAttribute('href');
      if (!url) return;
      var sectionId = this.getAttribute('data-section');
      var u = new URL(url, window.location.origin);
      var seq = this._renderSeq || 0;
      var epoch = this._domEpoch || 0;
      trigger.setAttribute('aria-busy', 'true');
      fetch(`${u.pathname}${u.search}${u.search ? '&' : '?'}section_id=${sectionId}`)
        .then((r) => r.text())
        .then((html) => {
          if (seq !== (this._renderSeq || 0) || epoch !== (this._domEpoch || 0)) return;
          if (!trigger.isConnected) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshGrid = doc.querySelector('.collection-grid');
          var grid = this.querySelector('.collection-grid');
          if (freshGrid && grid) {
            Array.from(freshGrid.children).forEach((node) => grid.appendChild(node));
          }
          var freshMore = doc.querySelector('[data-load-more]');
          var wrap = trigger.closest('.collection-load-more');
          if (freshGrid && freshMore) {
            trigger.setAttribute('href', freshMore.getAttribute('href'));
            trigger.setAttribute('aria-busy', 'false');
          } else if (freshGrid && wrap) {
            wrap.remove();
          } else {
            trigger.setAttribute('aria-busy', 'false');
          }
        })
        .catch(() => {
          if (seq !== (this._renderSeq || 0) || epoch !== (this._domEpoch || 0)) return;
          window.location.href = url;
        });
    }

    render(url, push, scroll) {
      // Read the section id when the request is built. A sort change replaces
      // the form before the next filter event, and the id must come from the
      // element that is in the document at that moment.
      var sectionId = this.getAttribute('data-section');
      var u = new URL(url, window.location.origin);
      // Latest request wins. An older section response must not replace a
      // newer grid, push its URL, or clear the loading state.
      var seq = (this._renderSeq || 0) + 1;
      this._renderSeq = seq;
      if (this._facetAbort) this._facetAbort.abort();
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      this._facetAbort = controller;
      this.setLoading(true);
      var fetchUrl = `${u.pathname}${u.search}${u.search ? '&' : '?'}section_id=${sectionId}`;
      fetch(fetchUrl, controller ? { signal: controller.signal } : undefined)
        .then((r) => r.text())
        .then((html) => {
          if (seq !== this._renderSeq) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-collection-inner]');
          var current = this.querySelector('[data-collection-inner]');
          if (fresh && current) {
            // Reveal any scroll-reveal content immediately. The replaced grid never
            // re-runs the IntersectionObserver, so without this its cards stay hidden.
            fresh.querySelectorAll('[data-reveal], [data-reveal-group]').forEach(function (el) {
              el.classList.add('is-visible');
            });
            current.replaceWith(fresh);
            this._domEpoch = (this._domEpoch || 0) + 1;
            if (!this._filtersOpener || !this._filtersOpener.isConnected) {
              this._filtersOpener = this.querySelector('[data-facet-toggle]');
            }
            // The section markup ships the closed panel. Keep it inert only
            // while this drawer is actually closed.
            this.syncDrawer();
          }
          if (push) history.pushState({}, '', `${u.pathname}${u.search}`);
          if (scroll) this.scrollToResults();
          if (this._focusFilters) {
            this._focusFilters = false;
            var toggle = this.querySelector('[data-facet-toggle]');
            if (toggle) toggle.focus({ preventScroll: true });
          }
          this.setLoading(false);
        })
        .catch((err) => {
          if (seq !== this._renderSeq) return;
          if (err && err.name === 'AbortError') return;
          window.location.href = url;
        });
    }

    scrollToResults() {
      var anchor = this.querySelector('.collection-toolbar') || this.querySelector('.collection-grid') || this.querySelector('[data-collection-inner]');
      if (!anchor) return;
      var header = document.querySelector('.site-header[data-sticky="true"]');
      var offset = header ? header.offsetHeight : 0;
      var rect = anchor.getBoundingClientRect();
      // Skip only when the toolbar top is already in the top half of the
      // viewport and below the sticky header. Lower on the screen still
      // scrolls. A zero box has no layout, so still scroll.
      var topHalf = window.innerHeight / 2;
      if ((rect.height > 0 || rect.width > 0) && rect.top >= offset && rect.top < topHalf) return;
      var top = rect.top + window.scrollY - offset - 8;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    }

    setLoading(on) {
      var inner = this.querySelector('[data-collection-inner]');
      if (inner) inner.setAttribute('aria-busy', on ? 'true' : 'false');
    }
  }

  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);
})();
